import {
  LayoutPanel,
  LEFT_PANEL_CONTENT,
  RIGHT_PANEL_CONTENT,
} from '@ui/pages/pipelinesWorkflow/components/PipelineLayoutManager'
import { create } from 'zustand'

interface LayoutPanelState {
  layoutModalConfig: {
    open: boolean
    content: JSX.Element | null
  }
  layoutPanelConfig: {
    [LayoutPanel.LEFT_PANEL]: {
      open: boolean
      content: LEFT_PANEL_CONTENT | null
      ignoreMaxConstraints: boolean
    }
    [LayoutPanel.INNER_LEFT_PANEL]: {
      open: boolean
      content: LEFT_PANEL_CONTENT | null
      ignoreMaxConstraints: boolean
    }
    [LayoutPanel.RIGHT_PANEL]: {
      open: boolean
      content: RIGHT_PANEL_CONTENT | null
      ignoreMaxConstraints: boolean
    }
    [LayoutPanel.INNER_RIGHT_PANEL]: {
      open: boolean
      content: RIGHT_PANEL_CONTENT | null
      ignoreMaxConstraints: boolean
    }
  }
}

interface LayoutPanelActions {
  togglePanel: (panel: LayoutPanel, open: boolean) => void
  reset: () => void
  setPanelContent: ({
    panel,
    content,
    open,
    ignoreMaxConstraints,
  }: {
    panel: LayoutPanel
    content: LEFT_PANEL_CONTENT | RIGHT_PANEL_CONTENT | null
    open?: boolean
    ignoreMaxConstraints?: boolean
  }) => void
  setModalContent: ({ content, open }: { content: JSX.Element | null; open: boolean }) => void
  setIgnoreMaxConstraints: (panel: LayoutPanel, ignoreMaxConstraints: boolean) => void
}

const initialState: LayoutPanelState = {
  layoutModalConfig: {
    open: false,
    content: null,
  },
  layoutPanelConfig: {
    [LayoutPanel.LEFT_PANEL]: {
      open: true,
      content: LEFT_PANEL_CONTENT.LIVE_SAMPLE,
      ignoreMaxConstraints: true,
    },
    [LayoutPanel.INNER_LEFT_PANEL]: {
      open: false,
      content: null,
      ignoreMaxConstraints: true,
    },
    [LayoutPanel.RIGHT_PANEL]: {
      open: false,
      content: null,
      ignoreMaxConstraints: false,
    },
    [LayoutPanel.INNER_RIGHT_PANEL]: {
      open: false,
      content: null,
      ignoreMaxConstraints: false,
    },
  },
}

export const useLayoutPanelManagerStore = create<LayoutPanelState & LayoutPanelActions>((set) => ({
  // STATE
  layoutModalConfig: {
    open: false,
    content: null,
  },
  layoutPanelConfig: {
    [LayoutPanel.LEFT_PANEL]: {
      open: true,
      content: LEFT_PANEL_CONTENT.INSIGHTS,
      ignoreMaxConstraints: false,
    },
    [LayoutPanel.INNER_LEFT_PANEL]: {
      open: false,
      content: LEFT_PANEL_CONTENT.LIVE_SAMPLE,
      ignoreMaxConstraints: true,
    },
    [LayoutPanel.RIGHT_PANEL]: {
      open: false,
      content: RIGHT_PANEL_CONTENT.TRANSFORMATION_CODE,
      ignoreMaxConstraints: false,
    },
    [LayoutPanel.INNER_RIGHT_PANEL]: {
      open: false,
      content: RIGHT_PANEL_CONTENT.TRANSFORMATION_CODE,
      ignoreMaxConstraints: false,
    },
  },
  // ACTIONS
  togglePanel: (panel: LayoutPanel, open: boolean) => {
    set((state) => {
      return {
        layoutPanelConfig: {
          ...state.layoutPanelConfig,
          [panel]: { ...state.layoutPanelConfig[panel], open: open },
        },
      }
    })
  },
  reset: () => {
    set(initialState)
  },
  setPanelContent: ({
    panel,
    content,
    open,
    ignoreMaxConstraints,
  }: {
    panel: LayoutPanel
    content: LEFT_PANEL_CONTENT | RIGHT_PANEL_CONTENT | null
    open?: boolean
    ignoreMaxConstraints?: boolean
  }) => {
    set((state) => {
      return {
        layoutPanelConfig: {
          ...state.layoutPanelConfig,
          [panel]: {
            ...state.layoutPanelConfig[panel],
            content: content,
            ignoreMaxConstraints: ignoreMaxConstraints ?? state.layoutPanelConfig[panel].ignoreMaxConstraints,
            open: open ?? state.layoutPanelConfig[panel].open,
          },
        },
      }
    })
  },
  setModalContent: ({ content, open }: { content: JSX.Element | null; open: boolean }) => {
    set((state) => {
      return {
        layoutModalConfig: { ...state.layoutModalConfig, content: content, open: open },
      }
    })
  },
  setIgnoreMaxConstraints: (panel: LayoutPanel, ignoreMaxConstraints: boolean) => {
    set((state) => {
      return {
        layoutPanelConfig: {
          ...state.layoutPanelConfig,
          [panel]: { ...state.layoutPanelConfig[panel], ignoreMaxConstraints: ignoreMaxConstraints },
        },
      }
    })
  },
}))
